import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import Pagination from '../../../../components/pagination/Pagination'
import './RequestLogs.scss'

interface RequestLog {
  _id: string
  actorType: 'admin' | 'user' | 'business' | 'anonymous'
  actorId?: string
  actorName?: string
  actorEmail?: string
  actorRole?: string
  method: string
  path: string
  statusCode: number
  durationMs: number
  ip?: string
  userAgent?: string
  query?: any
  params?: any
  body?: any
  createdAt: string
}

const EMPTY_FILTERS = {
  path: '',
  actorEmail: '',
  actorType: '',
  method: '',
  statusCode: '',
  from: '',
  to: '',
}

const ACTOR_META: any = {
  admin: { icon: 'mdi:shield-account', label: 'Admin' },
  user: { icon: 'mdi:account', label: 'Customer' },
  business: { icon: 'mdi:briefcase', label: 'Business' },
  anonymous: { icon: 'mdi:incognito', label: 'Anonymous' },
}

function RequestLogs() {
  const { notify, profile } = useGlobalContext()
  const [data, setData] = useState<any>({
    docs: [],
    totalDocs: 0,
    totalPages: 0,
    page: 1,
    limit: 20,
  })
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS })
  const [selectedLog, setSelectedLog] = useState<RequestLog | null>(null)

  function buildQuery(page: number, pageSize: number) {
    let url = `admin/request-logs?page=${page}&limit=${pageSize}`
    if (filters.path) url += `&path=${encodeURIComponent(filters.path)}`
    if (filters.actorEmail)
      url += `&actorEmail=${encodeURIComponent(filters.actorEmail)}`
    if (filters.actorType) url += `&actorType=${filters.actorType}`
    if (filters.method) url += `&method=${filters.method}`
    if (filters.statusCode) url += `&statusCode=${filters.statusCode}`
    if (filters.from) url += `&from=${filters.from}`
    if (filters.to) url += `&to=${filters.to}`
    return url
  }

  function fetchLogs(page: number, pageSize: number) {
    setLoading(true)
    get(buildQuery(page, pageSize))
      .then((result: any) => {
        if (result.status === 200) {
          setData(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch activity logs')
      })
  }

  useEffect(() => {
    fetchLogs(1, 20)
  }, [])

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const resetFilters = () => {
    setFilters({ ...EMPTY_FILTERS })
  }

  const initials = (log: RequestLog) => {
    const source = log.actorName || log.actorEmail || '?'
    return source
      .split(' ')
      .map((w: string) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  const statusClass = (code: number) => {
    if (code >= 500) return 'status-error'
    if (code >= 400) return 'status-warn'
    if (code >= 300) return 'status-info'
    return 'status-ok'
  }

  const timeAgo = (dateString: string) => {
    const seconds = Math.floor(
      (Date.now() - new Date(dateString).getTime()) / 1000,
    )
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })

  const shortPath = (path: string) =>
    path.replace(/^\/api\/v1/, '').split('?')[0]

  const hasPayload = (obj: any) => obj && Object.keys(obj).length > 0

  if (profile?.role !== 'super admin') {
    return (
      <div className='request-logs'>
        <div className='restricted'>
          <Icon icon='mdi:shield-lock' />
          <h1>Restricted</h1>
          <p>Activity logs are only available to super admins.</p>
        </div>
      </div>
    )
  }

  return (
    <div className='request-logs'>
      {selectedLog && (
        <div className='log-modal-overlay' onClick={() => setSelectedLog(null)}>
          <div className='log-modal' onClick={(e) => e.stopPropagation()}>
            <div className='log-modal-header'>
              <div className='header-left'>
                <span className={`chip method-${selectedLog.method}`}>
                  {selectedLog.method}
                </span>
                <span className={`chip ${statusClass(selectedLog.statusCode)}`}>
                  {selectedLog.statusCode}
                </span>
                <span className='duration'>{selectedLog.durationMs}ms</span>
              </div>
              <Icon
                icon='mdi:close'
                className='close'
                onClick={() => setSelectedLog(null)}
              />
            </div>
            <div className='log-modal-path'>{selectedLog.path}</div>
            <div className='log-modal-body'>
              <div className='detail-grid'>
                <div className='detail-card'>
                  <p className='label'>
                    <Icon icon={ACTOR_META[selectedLog.actorType]?.icon} />
                    Actor
                  </p>
                  <h3>{selectedLog.actorName || 'Unknown'}</h3>
                  <span>{selectedLog.actorEmail || '—'}</span>
                  <span className='sub'>
                    {ACTOR_META[selectedLog.actorType]?.label}
                    {selectedLog.actorRole ? ` · ${selectedLog.actorRole}` : ''}
                  </span>
                </div>
                <div className='detail-card'>
                  <p className='label'>
                    <Icon icon='mdi:web' />
                    Network
                  </p>
                  <h3>{selectedLog.ip || '—'}</h3>
                  <span className='sub agent'>
                    {selectedLog.userAgent || 'No user agent'}
                  </span>
                </div>
                <div className='detail-card'>
                  <p className='label'>
                    <Icon icon='mdi:clock-outline' />
                    Time
                  </p>
                  <h3>{formatDate(selectedLog.createdAt)}</h3>
                  <span className='sub'>{timeAgo(selectedLog.createdAt)}</span>
                </div>
              </div>

              {hasPayload(selectedLog.body) && (
                <div className='payload'>
                  <p className='label'>Request Body</p>
                  <pre>{JSON.stringify(selectedLog.body, null, 2)}</pre>
                </div>
              )}
              {hasPayload(selectedLog.query) && (
                <div className='payload'>
                  <p className='label'>Query</p>
                  <pre>{JSON.stringify(selectedLog.query, null, 2)}</pre>
                </div>
              )}
              {hasPayload(selectedLog.params) && (
                <div className='payload'>
                  <p className='label'>Route Params</p>
                  <pre>{JSON.stringify(selectedLog.params, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className='hero'>
        <div className='hero-icon'>
          <Icon icon='mdi:radar' />
        </div>
        <div className='hero-text'>
          <h1>System Activity</h1>
          <p>
            Every request on the platform — who made it, from where, and what
            happened.
          </p>
        </div>
        <div className='hero-stat'>
          <h2>{data.totalDocs?.toLocaleString?.() || 0}</h2>
          <span>logged requests</span>
        </div>
      </div>

      <div className='filter-card'>
        <div className='filter-grid'>
          <div className='field'>
            <label>Endpoint</label>
            <div className='input-icon'>
              <Icon icon='mdi:magnify' />
              <input
                type='search'
                name='path'
                placeholder='e.g. cancel, export, login'
                value={filters.path}
                onChange={handleFilterChange}
                onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1, data.limit)}
              />
            </div>
          </div>
          <div className='field'>
            <label>Actor email</label>
            <div className='input-icon'>
              <Icon icon='mdi:account-search' />
              <input
                type='search'
                name='actorEmail'
                placeholder='e.g. idris@'
                value={filters.actorEmail}
                onChange={handleFilterChange}
                onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1, data.limit)}
              />
            </div>
          </div>
          <div className='field'>
            <label>Actor type</label>
            <select
              name='actorType'
              value={filters.actorType}
              onChange={handleFilterChange}
            >
              <option value=''>All</option>
              <option value='admin'>Admin</option>
              <option value='user'>Customer</option>
              <option value='business'>Business</option>
              <option value='anonymous'>Anonymous</option>
            </select>
          </div>
          <div className='field'>
            <label>Method</label>
            <select
              name='method'
              value={filters.method}
              onChange={handleFilterChange}
            >
              <option value=''>All</option>
              <option value='POST'>POST</option>
              <option value='PUT'>PUT</option>
              <option value='PATCH'>PATCH</option>
              <option value='DELETE'>DELETE</option>
              <option value='GET'>GET</option>
            </select>
          </div>
          <div className='field'>
            <label>Status</label>
            <select
              name='statusCode'
              value={filters.statusCode}
              onChange={handleFilterChange}
            >
              <option value=''>All</option>
              <option value='200'>200 OK</option>
              <option value='201'>201 Created</option>
              <option value='400'>400 Bad Request</option>
              <option value='403'>403 Forbidden</option>
              <option value='404'>404 Not Found</option>
              <option value='500'>500 Server Error</option>
            </select>
          </div>
          <div className='field'>
            <label>From</label>
            <input
              type='date'
              name='from'
              value={filters.from}
              onChange={handleFilterChange}
            />
          </div>
          <div className='field'>
            <label>To</label>
            <input
              type='date'
              name='to'
              value={filters.to}
              onChange={handleFilterChange}
            />
          </div>
          <div className='field actions'>
            <button className='apply' onClick={() => fetchLogs(1, data.limit)}>
              <Icon icon='mdi:filter-variant' /> Apply
            </button>
            <button className='reset' onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className='table-card'>
        {loading ? (
          <FormLoader position='center' />
        ) : data.docs?.length > 0 ? (
          <>
            <table className='logs-table'>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Method</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>IP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.docs.map((log: RequestLog) => (
                  <tr key={log._id} onClick={() => setSelectedLog(log)}>
                    <td className='time'>
                      <span className='ago'>{timeAgo(log.createdAt)}</span>
                      <span className='full'>{formatDate(log.createdAt)}</span>
                    </td>
                    <td>
                      <div className='actor'>
                        <div className={`avatar avatar-${log.actorType}`}>
                          {log.actorType === 'anonymous' ? (
                            <Icon icon='mdi:incognito' />
                          ) : (
                            initials(log)
                          )}
                        </div>
                        <div className='actor-info'>
                          <span className='name'>
                            {log.actorName || 'Anonymous'}
                          </span>
                          <span className='role'>
                            {log.actorRole ||
                              ACTOR_META[log.actorType]?.label ||
                              ''}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`chip method-${log.method}`}>
                        {log.method}
                      </span>
                    </td>
                    <td className='path'>
                      <span>{shortPath(log.path)}</span>
                    </td>
                    <td>
                      <span className={`chip ${statusClass(log.statusCode)}`}>
                        {log.statusCode}
                      </span>
                    </td>
                    <td className='ip'>{log.ip || '—'}</td>
                    <td className='view-cell'>
                      <Icon icon='mdi:chevron-right' />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className='mobile-log-cards'>
              {data.docs.map((log: RequestLog) => (
                <div
                  className='log-card'
                  key={log._id}
                  onClick={() => setSelectedLog(log)}
                >
                  <div className='card-top'>
                    <span className={`chip method-${log.method}`}>
                      {log.method}
                    </span>
                    <span className={`chip ${statusClass(log.statusCode)}`}>
                      {log.statusCode}
                    </span>
                    <span className='ago'>{timeAgo(log.createdAt)}</span>
                  </div>
                  <p className='card-path'>{shortPath(log.path)}</p>
                  <div className='card-bottom'>
                    <span>{log.actorName || 'Anonymous'}</span>
                    <span>{log.ip || ''}</span>
                  </div>
                </div>
              ))}
            </div>

            <Pagination
              currentPage={data.page}
              totalCount={data.totalPages}
              pageSize={data.limit}
              onPageChange={(page: number, pageSize: number) => {
                fetchLogs(page, pageSize)
              }}
            />
          </>
        ) : (
          <div className='empty-logs'>
            <Icon icon='mdi:radar' />
            <h1>No activity found</h1>
            <p>Try widening your filters or date range.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default RequestLogs
